import { getAuthHeaders } from '../../statistics/api/auth-headers';

/**
 * Логотипы клиентов для титула и угла слайдов — общие для команды, лежат на
 * бэкенде отчётов рядом с контактами (/api/presentation/logos).
 *
 * Картинка передаётся data-URL в теле запроса: логотип весит килобайты, и
 * отдельная загрузка файла ради него — лишний маршрут и лишний повод
 * рассинхронизировать запись и картинку.
 */

export type LogoTemplateInput = {
  name: string;
  /** data-URL. При обновлении можно не передавать — останется прежняя. */
  image?: string;
};

export type LogoTemplate = {
  id: string;
  name: string;
  /** Постоянная ссылка на картинку в Storage. */
  imageUrl: string;
  createdAt: string;
  updatedAt: string;
};

const BASE = '/api/presentation/logos';

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const headers = await getAuthHeaders();
  // Fastify отвечает 400 на JSON-заголовок при пустом теле (DELETE, GET).
  if (init.body === undefined) delete headers['Content-Type'];
  const res = await fetch(url, { ...init, headers: { ...headers, ...init.headers } });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Ошибка сервера: ${res.status}`);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/** Файл → data-URL: логотипы маленькие, отдельная загрузка не нужна. */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'));
    reader.readAsDataURL(file);
  });
}

export const logoTemplatesApi = {
  async list(): Promise<LogoTemplate[]> {
    return (await request<{ logos: LogoTemplate[] }>(BASE)).logos;
  },

  async create(input: LogoTemplateInput): Promise<LogoTemplate> {
    const body = JSON.stringify(input);
    return (await request<{ logo: LogoTemplate }>(BASE, { method: 'POST', body })).logo;
  },

  async update(id: string, input: LogoTemplateInput): Promise<LogoTemplate> {
    const body = JSON.stringify(input);
    const url = `${BASE}/${encodeURIComponent(id)}`;
    return (await request<{ logo: LogoTemplate }>(url, { method: 'PUT', body })).logo;
  },

  async remove(id: string): Promise<void> {
    await request<void>(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};
